import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthDialog, type AuthMode } from './AuthDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ChildSigninDialog } from './ChildSigninDialog';
import { ChildrenDialog } from './ChildrenDialog';
import { ClassSigninDialog } from './ClassSigninDialog';
import { useCurrentUser, useDeleteAccount, useSignout } from './useAuth';
import './AccountChip.css';

/**
 * Header affordance for sign-in / signed-in state.
 *
 * Signed out: "Sign in" + "New here" buttons that open the AuthDialog.
 * Signed in: a chip showing the kid's email + a menu with Sign out
 * and Delete account.
 *
 * Keeps the local-first promise visible — when signed out, the chip
 * does NOT nag the kid to make an account.
 */
export function AccountChip(): JSX.Element | null {
  const navigate = useNavigate();
  const { data: user, isPending } = useCurrentUser();
  const signout = useSignout();
  const deleteAccount = useDeleteAccount();
  const [dialogMode, setDialogMode] = useState<AuthMode | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [kidSigninOpen, setKidSigninOpen] = useState(false);
  const [classSigninOpen, setClassSigninOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Other pages dispatch `bryta:open-signin` (e.g. the
  // "Sign in to publish" nudge on ProjectView) when they need the
  // sign-in dialog without taking a hard dependency on this
  // component's API. Listening here keeps that surface a one-line
  // CustomEvent rather than a lifted-up store.
  useEffect(() => {
    function open(): void {
      setDialogMode('signin');
    }
    window.addEventListener('bryta:open-signin', open);
    return () => window.removeEventListener('bryta:open-signin', open);
  }, []);

  // Don't render anything until we know the auth state — avoids a
  // flash of "Sign in" when the kid is already authed.
  if (isPending) return null;

  if (!user) {
    return (
      <>
        <div className="bl-account">
          <button
            type="button"
            className="bl-account__btn bl-account__btn--secondary"
            onClick={() => setClassSigninOpen(true)}
          >
            Class code
          </button>
          <button
            type="button"
            className="bl-account__btn bl-account__btn--secondary"
            onClick={() => setKidSigninOpen(true)}
          >
            Kid sign in
          </button>
          <button
            type="button"
            className="bl-account__btn bl-account__btn--secondary"
            onClick={() => setDialogMode('signin')}
          >
            Sign in
          </button>
          <button type="button" className="bl-account__btn" onClick={() => setDialogMode('signup')}>
            New here
          </button>
        </div>
        <AuthDialog
          isOpen={dialogMode !== null}
          {...(dialogMode ? { initialMode: dialogMode } : {})}
          onClose={() => setDialogMode(null)}
        />
        <ChildSigninDialog isOpen={kidSigninOpen} onClose={() => setKidSigninOpen(false)} />
        <ClassSigninDialog isOpen={classSigninOpen} onClose={() => setClassSigninOpen(false)} />
      </>
    );
  }

  const handleDeleteClick = (): void => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    deleteAccount.mutate(undefined, {
      onSuccess: () => {
        setMenuOpen(false);
        setConfirmingDelete(false);
      },
    });
  };

  // Child accounts have a synthetic `<id>+slug@bryta-children.invalid`
  // email the kid never sees. Show their display name instead so the
  // chip reads "Avery" not a UUID.
  // The `?? '?'` fallback covers a malformed `/auth/me` response — the
  // chip rendered an empty avatar and crashed with `charAt of undefined`
  // before this guard. We never expect this in production, but it keeps
  // tests + tab-restored sessions from fataling on a stale shape.
  const chipLabel = (user.is_child && user.display_name ? user.display_name : user.email) ?? '?';
  return (
    <div className="bl-account">
      <button
        type="button"
        className="bl-account__chip"
        onClick={() => setMenuOpen((m) => !m)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className="bl-account__avatar" aria-hidden="true">
          {chipLabel.charAt(0).toUpperCase()}
        </span>
        <span className="bl-account__email">{chipLabel}</span>
      </button>
      {menuOpen && (
        <div className="bl-account__menu" role="menu">
          {(user.role === 'moderator' || user.role === 'admin') && (
            <button
              type="button"
              role="menuitem"
              className="bl-account__menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigate('/admin/moderator');
              }}
            >
              Moderator queue
            </button>
          )}
          {/* Profile link for users who already claimed a username,
              else a nudge to claim one. The claim dialog lives on
              /studio so we just navigate there with a hash that
              `Home` opens — keeps this component dialog-free. */}
          {user.username ? (
            <button
              type="button"
              role="menuitem"
              className="bl-account__menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigate(`/u/${user.username}`);
              }}
            >
              View my profile
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="bl-account__menu-item"
              onClick={() => {
                setMenuOpen(false);
                navigate('/studio#claim-username');
              }}
            >
              Pick a username →
            </button>
          )}
          {user.is_child !== true && (
            <button
              type="button"
              role="menuitem"
              className="bl-account__menu-item"
              onClick={() => {
                setMenuOpen(false);
                setChildrenOpen(true);
              }}
            >
              Your kids
            </button>
          )}
          {user.is_child !== true && (
            <button
              type="button"
              role="menuitem"
              className="bl-account__menu-item"
              onClick={() => {
                setMenuOpen(false);
                setChangePasswordOpen(true);
              }}
            >
              Change password
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="bl-account__menu-item"
            onClick={() => {
              setMenuOpen(false);
              void signout.mutateAsync();
            }}
            disabled={signout.isPending || deleteAccount.isPending}
          >
            {signout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="bl-account__menu-item bl-account__menu-item--danger"
            onClick={handleDeleteClick}
            disabled={signout.isPending || deleteAccount.isPending}
          >
            {deleteAccount.isPending
              ? 'Deleting…'
              : confirmingDelete
                ? 'Click again to confirm — this is permanent'
                : 'Delete my account'}
          </button>
          {deleteAccount.error && (
            <p role="alert" className="bl-account__menu-error">
              {deleteAccount.error.message}
            </p>
          )}
        </div>
      )}
      <ChildrenDialog isOpen={childrenOpen} onClose={() => setChildrenOpen(false)} />
      <ChangePasswordDialog
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}
